import { useEffect, useMemo, useState } from "react";
import type { ReactNode, MouseEvent } from "react";
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableContainer,
  Typography,
  tableBodyClasses,
  tableCellClasses,
  tableContainerClasses,
  tableRowClasses,
} from "@mui/material";
import type { Perimetre, Personne, Commune } from "models";
import { communesService, mandatsService } from "services";
import PerimetreDeleguesSdeaSkeleton from "./PerimetreDeleguesSdeaSkeleton";
import PerimetreDeleguesSdeaRow from "./PerimetreDeleguesSdeaRow";
import Fonctions from "constants/Fonctions";
import { elusUtil, excelUtil } from "utils";
import {
  useErrorHandler,
  withComponentErrorBoundary,
  useSnackbarErrorHandler,
} from "utils/errorHandling";
import { Authorization } from "@sdeapps/react-core";
import { ApplicationRole } from "constants/ApplicationRole";
import PerimetreDeleguesSdeaHeader from "./PerimetreDeleguesSdeaHeader";
import type { ColumnKeyName, Order, PerimetreDelegueRow } from "./PerimetreDeleguesSdeaHeader";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

type ComparatorFunc<Key extends keyof ColumnKeyName> = (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number;

function ascendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  if (typeof a[orderBy] === "string" && typeof b[orderBy] === "string") {
    return a[orderBy].localeCompare(b[orderBy]);
  }

  if (typeof a[orderBy] === "number" && typeof b[orderBy] === "number") {
    return (a[orderBy] as number) - (b[orderBy] as number);
  }

  return 0;
}

function getComparator<K extends keyof ColumnKeyName>(order: Order, orderBy: K): ComparatorFunc<K> {
  return order === "desc"
    ? (a, b) => -ascendingComparator(a, b, orderBy)
    : (a, b) => ascendingComparator(a, b, orderBy);
}

interface PerimetreDeleguesSdeaProps {
  perimetre: Perimetre;
}

function PerimetreDeleguesSdea({ perimetre }: Readonly<PerimetreDeleguesSdeaProps>): ReactNode {
  const [elus, setElus] = useState<Array<Personne>>([]);
  const [communes, setCommunes] = useState<Array<Commune>>([]);

  const [tableElusMandatCommune, setTableElusMandatCommune] = useState<Array<PerimetreDelegueRow>>(
    []
  );
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof ColumnKeyName>("mandatsWeight");

  const { catchErrors: catchDatasErrors, isLoading: isDatasLoading } = useErrorHandler();
  const { catchErrors: catchExcelErrors, isLoading: isExcelLoading } = useSnackbarErrorHandler();

  function handleRequestSort(_: MouseEvent<unknown>, p: keyof ColumnKeyName): void {
    const isAsc = orderBy === p && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(p);
  }

  const tableSort = useMemo<Array<PerimetreDelegueRow>>(
    () => tableElusMandatCommune.toSorted(getComparator(order, orderBy)),
    [order, orderBy, tableElusMandatCommune]
  );

  useEffect(() => {
    async function getElus(): Promise<void> {
      setTableElusMandatCommune([]);
      setElus([]);
      setCommunes([]);

      const [_perimetreMandats, _communes] = await Promise.all([
        mandatsService.getByFilter({
          perimetresIds: [perimetre.id],
          fonctionsIds: [
            Fonctions.PRESIDENT_COMMISSION_LOCALE,
            Fonctions.SUPPLEANT_PRESIDENT_COMMISSION_LOCALE,
            Fonctions.DELEGUE_ASSEMBLEE_GENERALE,
          ],
          isActive: true,
        }),
        communesService.getByPerimetre(perimetre.id),
      ]);

      const _elus = elusUtil
        .getElusFromMandats(_perimetreMandats)
        .toSorted(elusUtil.sortElusByMandatImportance);

      const _tableElusMandatCommune: Array<PerimetreDelegueRow> = _elus.map((e, i) => {
        const codeCollectivite = e.mandats?.map((m) => m.codeCollectivite).find((c) => c != null);
        const commune = _communes?.find((c) => c.id === codeCollectivite);
        return {
          elu: {
            id: e.id,
            nom: e.nom,
            prenom: e.prenom,
            dateNaissance: e.dateNaissance,
            source: e.source,
          },
          eluName: `${e.nom} ${e.prenom}`,
          mandatsWeight: i,
          mandats: e.mandats,
          communeName: commune?.libelle ?? "",
          commune,
        } satisfies PerimetreDelegueRow;
      });

      setTableElusMandatCommune(_tableElusMandatCommune);
      setElus(_elus);
      setCommunes(_communes);
    }

    void catchDatasErrors(getElus);
  }, [catchDatasErrors, perimetre]);

  function createFeuillePresence(): void {
    void catchExcelErrors(async (): Promise<void> => {
      await excelUtil.createFeuillePresencePerimetre(perimetre, elus, communes);
    });
  }

  if (isDatasLoading) {
    return <PerimetreDeleguesSdeaSkeleton />;
  }

  if (tableSort.length === 0) {
    return (
      <Grid size={12}>
        <Typography>Ce périmètre SDEA n'a aucun délégué.</Typography>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} size={12}>
      <Grid size="grow">
        <Typography>
          <Typography component="span" sx={{ fontWeight: "600" }}>
            {tableSort.length}
          </Typography>{" "}
          membre{tableSort.length > 1 ? "s" : ""} du périmètre SDEA :
        </Typography>
      </Grid>
      <Authorization roles={[ApplicationRole.REPORTS_READ_ALL]}>
        <Grid size={{ xs: 12, md: "auto" }}>
          <Button
            variant="contained"
            onClick={createFeuillePresence}
            loading={isExcelLoading}
            loadingPosition="start"
            startIcon={<FileDownloadIcon />}>
            Exporter la feuille de présence
          </Button>
        </Grid>
      </Authorization>
      <Grid size={12}>
        <TableContainer
          sx={{
            [`&.${tableContainerClasses.root} .${tableBodyClasses.root} .${tableRowClasses.root}:last-child .${tableCellClasses.root}`]:
              { border: "none" },
          }}>
          <Table>
            <PerimetreDeleguesSdeaHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {tableSort?.map((row: PerimetreDelegueRow) => (
                <PerimetreDeleguesSdeaRow
                  key={row.elu.id}
                  importantFonctions={[Fonctions.PRESIDENT_COMMISSION_LOCALE]}
                  data={row}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
}

const PerimetreDeleguesSdeaWithErrorBoundary = withComponentErrorBoundary(PerimetreDeleguesSdea);
export default PerimetreDeleguesSdeaWithErrorBoundary;
